import { cx } from '@emotion/css';
import type { FC } from 'react';
import { useContext } from 'react';

import { dataSetToAttributes } from '../../utils';
import { Sticker } from '../Sticker/Sticker';
import { addSrcFactory } from './Picture';
import { PictureContext } from './PictureContext';
import { pictureContainerCss } from './styles';
import type { PictureWithStickersProps } from './types';

export const PictureWithStickers: FC<PictureWithStickersProps> = ({
  altText,
  className,
  style,
  defaultSrc,
  imgClassName,
  imgSrcs,
  height,
  width,
  fetchPriority,
  dataset,
  isDraggable,
  handleSafariSourceSets,
  stickers = [],
}) => {
  const hasNoSources = !imgSrcs?.sources?.length;
  const pictureContext = useContext(PictureContext);

  return (
    <span className={cx(pictureContainerCss, className)} style={style}>
      <picture>
        {imgSrcs?.sources?.map((imgSrc, index) => (
          <source
            srcSet={imgSrc.url}
            type={imgSrc.type}
            key={`${imgSrc.type}${index}desktop`}
            sizes={imgSrc.sizes}
            media={imgSrc.media}
          />
        ))}
        <img
          ref={
            handleSafariSourceSets
              ? addSrcFactory(imgSrcs?.default ?? defaultSrc, imgSrcs?.defaultSrcSet)
              : undefined
          }
          alt={altText ?? ''} // default to an empty string to ensure always render an alt attribute
          className={imgClassName}
          sizes={imgSrcs?.defaultSizes}
          height={height}
          width={width}
          // Render with src + srcSets attribute set if there is no risk of Safari loading multiple images (which only happens
          // when both src and srcSet are set on an <img> tag) or if consumer has opted out of the workaround.
          src={hasNoSources || !handleSafariSourceSets ? imgSrcs?.default ?? defaultSrc : undefined}
          srcSet={hasNoSources || !handleSafariSourceSets ? imgSrcs?.defaultSrcSet : undefined}
          loading={pictureContext?.lazy ? 'lazy' : undefined}
          // @ts-ignore this attribute is chrome only and doesn't exist (as of November 2023) on typings yet because its so new
          // eslint-disable-next-line react/no-unknown-property
          fetchpriorty={fetchPriority}
          {...dataSetToAttributes(dataset)}
          draggable={isDraggable}
        />
      </picture>
      {stickers?.map((props, i) => (
        <Sticker key={i} {...props} />
      ))}
    </span>
  );
};
